import { PricingConfig } from '@/types/pricing';

/**
 * Pricing Configuration - Monthly-Only, Per-Seat Model
 * 
 * Pricing Structure (Early Access):
 * - Free: $0/mo (limited features)
 * - Starter: $79/mo (single user)
 * - Growth: $199/mo base + $29/mo per seat (3+ users)
 * - Team: $499/mo base + $49/mo per seat (5+ users)
 * - Pro: $1,500/mo base + $75/mo per seat (10+ users)
 */
export const PRICING_CONFIG: PricingConfig = {
  free: {
    name: 'Free',
    tier: 'free',
    basePrice: 0,
    stripePriceBase: '', // No Stripe price for free tier
    tagline: 'Get your VoiceDNA™ and start posting',
    limits: {
      postsPerMonth: 6,
      connectedAccounts: 1, // LinkedIn only
      teamMembers: 1,
      aiImages: 0,
      contentDNA: {
        sources: 1,
        brandVoice: false,
        assimilation: false,
      },
    },
    features: {
      contentAI: [
        '6 posts per month',
        'Basic VoiceDNA™ scan',
        'LinkedIn-only publishing',
      ],
      publishing: [
        'LinkedIn publishing',
        'Basic scheduling',
      ],
      analytics: [
        'Basic engagement metrics',
      ],
      support: [
        'Online support',
      ],
    }
  },
  starter: {
    name: 'Starter',
    tier: 'starter',
    basePrice: 79,
    stripePriceBase: 'price_1SYiGHExBHL5J2IJH8TXn89i',
    tagline: 'For solo founders testing thought leadership',
    limits: {
      postsPerMonth: 30,
      connectedAccounts: 2,
      teamMembers: 1,
      aiImages: 10,
      contentDNA: {
        sources: 5,
        brandVoice: false,
        assimilation: false,
      },
    },
    features: {
      contentAI: [
        'Post generation (30/month)',
        '10 generated images per month',
        'Basic ContentDNA™ (5 sources)',
        'Founder role only',
      ],
      publishing: [
        '30 posts per month',
        '2 connected social accounts',
        'Post scheduling',
        '3 campaign templates',
      ],
      analytics: [
        'Basic engagement metrics',
        'Post performance tracking',
        'Link click reporting',
      ],
      team: [
        'Single user',
      ],
      support: [
        'Email support',
      ],
    }
  },
  growth: {
    name: 'Growth',
    tier: 'growth',
    basePrice: 199,
    perSeatPrice: 29,
    stripePriceBase: 'price_1Sb6bUExBHL5J2IJFUGdPfeQ',
    stripePriceSeat: 'price_1Sb6duExBHL5J2IJS7QzzjB0',
    tagline: 'Small teams building aligned thought leadership',
    popular: true,
    minSeats: 3,
    limits: {
      postsPerMonth: 'Unlimited',
      connectedAccounts: 'Unlimited',
      teamMembers: '3+',
      aiImages: 'Unlimited',
      contentDNA: {
        sources: 'Unlimited',
        brandVoice: true,
        assimilation: true,
      },
    },
    features: {
      contentAI: [
        'Unlimited posts',
        'Unlimited generated images',
        'Full ContentDNA™ engine',
        'Brand voice guardrails',
        'Multi-source ingestion',
        '10 professional roles',
      ],
      publishing: [
        'Unlimited posts',
        'Unlimited social accounts',
        'Advanced scheduling',
        'All campaign templates',
        'Evergreen recycling',
      ],
      analytics: [
        'Trial signup tracking',
        'Weekly insights email',
        'Platform performance',
        'Team activity tracking',
      ],
      team: [
        '3+ team members',
        'Content approval workflow',
        'Role-based permissions',
        'Pre-approved content library',
      ],
      support: [
        'Priority email support',
        'Onboarding assistance',
      ],
    }
  },
  team: {
    name: 'Team',
    tier: 'team',
    basePrice: 499,
    perSeatPrice: 49,
    stripePriceBase: 'price_1Sb6gwExBHL5J2IJHhHBcQFK',
    stripePriceSeat: 'price_1Sb6h6ExBHL5J2IJPqfBJ4tm',
    tagline: 'Pipeline attribution for growing teams',
    minSeats: 5,
    limits: {
      postsPerMonth: 'Unlimited',
      connectedAccounts: 'Unlimited',
      teamMembers: '5+',
      aiImages: 'Unlimited',
      contentDNA: {
        sources: 'Unlimited',
        brandVoice: true,
        assimilation: true,
      },
    },
    features: {
      contentAI: [
        'Everything in Growth',
        'Advanced content repurposing',
        'Custom campaign builder',
        'A/B testing',
        'All 25 professional roles',
      ],
      publishing: [
        'Everything in Growth',
        'Multi-product support',
        'Advanced automation rules',
      ],
      analytics: [
        'Everything in Growth',
        'Trial-to-paid conversion',
        'Revenue attribution',
        'Campaign ROI calculator',
        'Monthly executive reports',
      ],
      team: [
        '5+ team members',
        'Employee leaderboards',
        'Attribution per employee',
        'Advanced RBAC',
      ],
      integrations: [
        'HubSpot integration',
        'Salesforce integration',
        'CRM deal tracking',
        'Webhooks & API access',
      ],
      support: [
        'Priority support',
        'Dedicated onboarding',
      ],
    }
  },
  pro: {
    name: 'Pro',
    tier: 'pro',
    basePrice: 1500,
    perSeatPrice: 75,
    stripePriceBase: 'price_1Sb6hAExBHL5J2IJdy3Lcrwy',
    stripePriceSeat: 'price_1Sb6hBExBHL5J2IJytuUG7hl',
    tagline: 'Advanced features for scaling organizations',
    minSeats: 10,
    limits: {
      postsPerMonth: 'Unlimited',
      connectedAccounts: 'Unlimited',
      teamMembers: '10+',
      aiImages: 'Unlimited',
      contentDNA: {
        sources: 'Unlimited',
        brandVoice: true,
        assimilation: true,
      },
    },
    features: {
      contentAI: [
        'Everything in Team',
        'Custom content training',
        'Multi-brand support',
      ],
      publishing: [
        'Everything in Team',
        'Custom workflows',
        'White-label options',
      ],
      analytics: [
        'Everything in Team',
        'Real-time executive dashboards',
        'Board-ready presentations',
        'Predictive forecasting',
        'Custom reporting',
      ],
      team: [
        '10+ team members',
        'Full gamification suite',
        'Team collaboration workflows',
      ],
      integrations: [
        'Everything in Team',
        'Custom integrations',
        'Data warehouse export',
      ],
      security: [
        'SSO/SAML',
        'Advanced permissions',
        'Audit logs',
        'Compliance support',
      ],
      support: [
        'Dedicated success manager',
        'Custom onboarding',
        '24/7 priority support',
        'SLA guarantee',
      ],
    },
    cta: 'Contact Sales'
  }
};
